import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { useOrg } from "@/context/OrgContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ShieldCheck, Warning, PaperPlaneRight, Certificate } from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUS_COLORS = {
  unverified: "bg-secondary text-muted-foreground",
  in_review: "bg-accent/10 text-accent",
  verified: "bg-primary/10 text-primary",
  qualified: "bg-yellow-500/10 text-yellow-700",
  adverse: "bg-destructive/10 text-destructive",
};

export default function Verification() {
  const { t } = useLang();
  const { role } = useOrg();
  const [year, setYear] = useState(new Date().getFullYear());
  const [current, setCurrent] = useState(null);
  const [history, setHistory] = useState([]);
  const [notes, setNotes] = useState("");
  const [decision, setDecision] = useState({ status: "verified", opinion: "", level_of_assurance: "limited" });

  const load = async () => {
    const [c, h] = await Promise.all([
      api.get("/verifications/current", { params: { year } }),
      api.get("/verifications"),
    ]);
    setCurrent(c.data);
    setHistory(h.data);
  };

  useEffect(() => { load(); }, [year]);

  const submit = async () => {
    try {
      await api.post("/verifications/submit", { reporting_year: year, scopes: [1, 2, 3], notes: notes || null });
      toast.success(t("verificationSubmitted"));
      setNotes("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const issueDecision = async () => {
    if (!current?.id) return;
    try {
      await api.post(`/verifications/${current.id}/decision`, decision);
      toast.success(t("verificationDecided"));
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const canSubmit = role === "owner" || role === "editor";
  const canDecide = role === "verifier" && current?.status === "in_review";

  const statusKey = (current?.status || "unverified").replace("_", "");
  const statusLabel = t(statusKey) || current?.status;

  return (
    <div className="space-y-6 max-w-5xl">
      <div className="flex flex-wrap justify-between items-end gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono-data">{t("iso14064_3")}</div>
          <h1 className="font-display text-3xl font-semibold mt-1">{t("verification")}</h1>
        </div>
        <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v))}>
          <SelectTrigger className="w-[120px] font-mono-data" data-testid="verification-year">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {[0, 1, 2, 3, 4].map((n) => {
              const y = new Date().getFullYear() - n;
              return <SelectItem key={`ver-y-${y}`} value={String(y)}>{y}</SelectItem>;
            })}
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-start justify-between space-y-0">
          <div>
            <CardTitle className="font-display text-lg">{t("verificationStatus")} · {year}</CardTitle>
            {current?.submitted_at && (
              <div className="text-xs text-muted-foreground font-mono-data mt-1">
                Submitted {current.submitted_at.slice(0, 10)}
              </div>
            )}
          </div>
          <span
            className={`text-xs font-mono-data uppercase px-3 py-1.5 rounded-sm ${STATUS_COLORS[current?.status || "unverified"]}`}
            data-testid="verification-badge"
          >
            {statusLabel}
          </span>
        </CardHeader>
        <CardContent className="space-y-4">
          {current?.status === "unverified" ? (
            canSubmit ? (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  Submit the {year} GHG inventory for third-party verification. A verifier can then review the data and issue an opinion aligned with ISO 14064-3.
                </p>
                <div className="space-y-2">
                  <Label>Notes for verifier (optional)</Label>
                  <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="verification-notes" />
                </div>
                <Button onClick={submit} data-testid="verification-submit">
                  <PaperPlaneRight size={16} className="mr-2" /> {t("submitForVerification")}
                </Button>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                An owner or editor of this organization needs to submit the inventory for verification.
              </p>
            )
          ) : (
            <div className="space-y-3">
              <Field label="Total emissions submitted" value={`${current?.total_t_co2e ?? "—"} tCO₂e`} />
              <Field label={t("levelOfAssurance")} value={t(current?.level_of_assurance) || current?.level_of_assurance || "—"} />
              {current?.verifier_name && (
                <Field label={t("verifiedBy")} value={`${current.verifier_name} ${current.decided_at ? `· ${current.decided_at.slice(0, 10)}` : ""}`} />
              )}
              {current?.opinion && (
                <div>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono-data">{t("opinion")}</div>
                  <div className="border-l-2 border-primary/40 pl-3 mt-1 text-sm leading-relaxed">{current.opinion}</div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {canDecide && (
        <Card className="border-primary/40">
          <CardHeader>
            <CardTitle className="font-display text-lg flex items-center gap-2">
              <Certificate size={20} weight="duotone" className="text-primary" /> {t("verifierDecision")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4" data-testid="verifier-panel">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={decision.status} onValueChange={(v) => setDecision({ ...decision, status: v })}>
                  <SelectTrigger data-testid="decision-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="verified">{t("verified")}</SelectItem>
                    <SelectItem value="qualified">{t("qualified")}</SelectItem>
                    <SelectItem value="adverse">{t("adverse")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t("levelOfAssurance")}</Label>
                <Select value={decision.level_of_assurance} onValueChange={(v) => setDecision({ ...decision, level_of_assurance: v })}>
                  <SelectTrigger data-testid="decision-loa"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="limited">{t("limited")}</SelectItem>
                    <SelectItem value="reasonable">{t("reasonable")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t("opinion")}</Label>
              <Textarea
                rows={5}
                required
                value={decision.opinion}
                onChange={(e) => setDecision({ ...decision, opinion: e.target.value })}
                placeholder="Based on our review, the GHG inventory presented is free from material misstatement…"
                data-testid="decision-opinion"
              />
            </div>
            <Button onClick={issueDecision} data-testid="decision-submit">
              <ShieldCheck size={16} className="mr-2" /> {t("issueOpinion")}
            </Button>
          </CardContent>
        </Card>
      )}

      {history.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="font-display text-lg">History</CardTitle></CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-muted-foreground border-b border-border">
                <tr>
                  <th className="text-left px-4 py-2">{t("year")}</th>
                  <th className="text-left px-4 py-2">Status</th>
                  <th className="text-left px-4 py-2">{t("verifiedBy")}</th>
                  <th className="text-right px-4 py-2">tCO₂e</th>
                  <th className="text-left px-4 py-2">{t("levelOfAssurance")}</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h) => (
                  <tr key={h.id} className="border-b border-border/60">
                    <td className="px-4 py-2 font-mono-data">{h.reporting_year}</td>
                    <td className="px-4 py-2">
                      <span className={`text-[10px] font-mono-data uppercase px-2 py-1 rounded-sm ${STATUS_COLORS[h.status]}`}>{h.status}</span>
                    </td>
                    <td className="px-4 py-2">{h.verifier_name || "—"}</td>
                    <td className="px-4 py-2 text-right font-mono-data">{h.total_t_co2e ?? "—"}</td>
                    <td className="px-4 py-2 text-xs text-muted-foreground">{h.level_of_assurance || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

const Field = ({ label, value }) => (
  <div>
    <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono-data">{label}</div>
    <div className="mt-0.5 font-mono-data">{value}</div>
  </div>
);
